import {
  Button,
  ButtonProps,
  Card,
  Divider,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  TableRowId,
} from '@fluentui/react-components';
import { AddFilled, DismissRegular, DocumentArrowUpRegular, FolderArrowUpRegular } from '@fluentui/react-icons';
import { Dispatch, SetStateAction, useContext, useRef, useState } from 'react';
import * as yup from 'yup';

import { EBindingMode, TAction, TItem } from '../types/items.types';
import { getBindingModeOptions } from '../helpers/templateSelection.helper';
import { EFolderType, ECountriesFolderType, IFolder, ESnapshotsFolderType } from '../types/folders.types';
import { useCreate as useCreateFolder, useProductFolders } from '../api/folders.api';

import {
  Overflow,
  TOverflowButtonParam,
  UploadInput,
  TSingleFieldDialogState,
  OptionsDialog,
} from '@shared/components';
import { TMap, TOptionsDialogState } from '@shared/types';

import { useCreate as useCreateDoc } from '@modules/documents';
import { TSelectItemDialogState, useCopy } from '@modules/items';
import { AccessType } from '@modules/access';
import { USER_GROUPS, SignInContext } from '@modules/users';

// Assets
import folderIcon from '../assets/folder.svg';
import docx from '../assets/docx.svg';
import dotx from '../assets/dotx.svg';
import block from '../assets/block.svg';
import inlineBlock from '../assets/inline-block.svg';
import { ToastContext } from '@shared/contexts';
import { TERMINOLOGY } from '@shared/i18n';
import { useCreateInlineBlock, useCreateQualioInlineBlock } from '@modules/inlineBlocks';
import { TNameAndProductsDialogState } from './dialogs/NameAndProductsDialog';
import { buildHref } from '@shared/utils';
import { openInNewTab } from '@shared/helpers';
import { AUTO_EDIT_QUERY_PARAM, AUTO_EDIT_QUERY_VALUE } from '../constants';

type TProps = {
  folder: IFolder;
  selectedItemIds: Set<TableRowId>;
  setSelectedItemIds: (selectedItemIds: Set<TableRowId>) => void;
  actions: TAction[];
  setSimpleDialogState: (state: TSingleFieldDialogState<'input'>) => void;
  setNameAndProductsDialogState: (state: TNameAndProductsDialogState) => void;
  handleFilesUpload: (files: File[]) => void;
  isBlockFolderType: boolean;
  countriesFolderType: ECountriesFolderType | undefined;
  snapshotsFolderType: ESnapshotsFolderType | undefined;
  setTemplateDialogState: Dispatch<SetStateAction<TSelectItemDialogState | undefined>>;
};

const useStyles = makeStyles({
  card: {
    borderRadius: '12px',
    padding: '10px 14px',
  },
  transparent: {
    backgroundColor: 'transparent', // tokens.colorNeutralBackground3,  //'#f5f5f5',
    boxShadow: 'none',
  },
});

const Toolbar = ({
  folder,
  selectedItemIds,
  setSelectedItemIds,
  actions,
  setSimpleDialogState,
  setNameAndProductsDialogState,
  handleFilesUpload,
  isBlockFolderType,
  countriesFolderType,
  snapshotsFolderType,
  setTemplateDialogState,
}: TProps) => {
  const { user, organization } = useContext(SignInContext);
  const { show, showProgress } = useContext(ToastContext);

  const copy = useCopy();

  const openItemInNewTab = (
    item?: TItem,
    params: TMap<string, string | undefined> = { view: 'draft', [AUTO_EDIT_QUERY_PARAM]: AUTO_EDIT_QUERY_VALUE }
  ) => {
    if (!item) return;
    const path = item.type === 'INLINE_BLOCK' ? `/inline-block/${item._id}` : `/document/${item._id}`;
    const url = buildHref(path, params);
    setTimeout(() => openInNewTab(url), 200);
  };

  const templatesEnabled = organization.featureSettings.templates?.enabled;
  const canCreateFromTemplate =
    templatesEnabled &&
    !isBlockFolderType &&
    folder.accessRight >= AccessType.EDIT &&
    user.groups.includes(USER_GROUPS.AUTHOR);

  const selectedItems = folder.children.filter((item) => selectedItemIds.has(item._id));
  // filter out disabled actions
  const params: TOverflowButtonParam[] = actions
    .filter((a) => a.show(selectedItems) && a.enable(selectedItems))
    // transform actions to the format required by Overflow
    .map((a) => ({
      ...a,
      onClick: () => a.onClick(selectedItems),
    }));

  let newFolderLabel = 'New folder';
  if (countriesFolderType === ECountriesFolderType.ROOT)
    newFolderLabel = `New ${TERMINOLOGY.registrationCountry.lower}`;
  else if (countriesFolderType === ECountriesFolderType.COUNTRY)
    newFolderLabel = `New ${TERMINOLOGY.registrationSingle.lower}`;
  else if (snapshotsFolderType === ESnapshotsFolderType.ROOT)
    newFolderLabel = `New ${TERMINOLOGY.snapshotCollection.lower}`;
  else if (snapshotsFolderType === ESnapshotsFolderType.SNAPSHOT_COLLECTION)
    newFolderLabel = `New ${TERMINOLOGY.snapshot.lower}`;

  const createFolder = useCreateFolder();
  const productFolders = useProductFolders({ enabled: countriesFolderType === ECountriesFolderType.COUNTRY });
  const registrationProducts = productFolders.map((item) => ({ _id: item._id, name: item.name }));
  const canCreateCountryRegistration = registrationProducts.length > 0;

  const onSubmit = async (data: { text: string; products?: string[] }, onSuccess: () => void) => {
    const resolvedProductNames = data.products
      ? Object.fromEntries(
          data.products
            .map((id) => [id, registrationProducts.find((p) => p._id === id)?.name] as const)
            .filter((entry): entry is [string, string] => Boolean(entry[1]))
        )
      : undefined;
    await createFolder.mutateAsync(
      {
        parentId: folder._id,
        name: data.text,
        countryRegistration: data.products?.length ? { products: data.products } : undefined,
        resolvedProductNames,
      },
      { onSuccess }
    );
  };
  const handleFolderCreate = () => {
    // if we are creating a new registration, open a different dialog
    if (countriesFolderType === ECountriesFolderType.COUNTRY) {
      if (!canCreateCountryRegistration) {
        show('error', 'Products are not available yet. Please try again in a moment.');
        return;
      }
      const dialogState: TNameAndProductsDialogState = {
        isOpen: true,
        onSubmit: (data) => onSubmit(data, () => setNameAndProductsDialogState({ ...dialogState, isOpen: false })),
        products: registrationProducts,
      };
      setNameAndProductsDialogState(dialogState);
    } else {
      const dialogState: TSingleFieldDialogState<'input'> = {
        isOpen: true,
        defaultText: '',
        title: newFolderLabel,
        label: 'Name',
        buttonLabel: 'Create',
        onSubmit: (data) => onSubmit(data, () => setSimpleDialogState({ ...dialogState, isOpen: false })),
      };
      setSimpleDialogState(dialogState);
    }
  };

  const createDoc = useCreateDoc();
  const handleDocCreate = () => {
    const dialogState: TSingleFieldDialogState<'input'> = {
      isOpen: true,
      defaultText: '',
      title: `New ${isBlockFolderType ? TERMINOLOGY.block.lower : 'document'}`,
      label: 'Name',
      buttonLabel: 'Create',
      afterLabel: isBlockFolderType ? undefined : '.docx',
      onSubmit: async (data) => {
        await createDoc.mutateAsync(
          {
            parent: folder._id,
            name: `${data.text}${isBlockFolderType ? '' : '.docx'}`,
          },
          {
            onSuccess: (data) => {
              setSimpleDialogState({ ...dialogState, isOpen: false });
              openItemInNewTab(data);
            },
          }
        );
      },
    };
    setSimpleDialogState(dialogState);
  };

  const createInlineBlock = useCreateInlineBlock();
  const handleInlineBlockCreate = () => {
    const dialogState: TSingleFieldDialogState<'input'> = {
      isOpen: true,
      defaultText: '',
      title: `New ${TERMINOLOGY.inlineBlock.lower}`,
      label: 'Name',
      buttonLabel: 'Create',
      onSubmit: async (data) => {
        await createInlineBlock.mutateAsync(
          { parent: folder._id, name: data.text },
          {
            onSuccess: (data) => {
              setSimpleDialogState({ ...dialogState, isOpen: false });
              openItemInNewTab(data);
            },
          }
        );
      },
    };
    setSimpleDialogState(dialogState);
  };

  const createQualioInlineBlock = useCreateQualioInlineBlock();
  const handleQualioInlineBlockCreate = () => {
    const dialogState: TSingleFieldDialogState<'input'> = {
      isOpen: true,
      defaultText: '',
      title: `New Qualio ${TERMINOLOGY.inlineBlock.lower}`,
      label: 'Qualio link',
      buttonLabel: 'Create',
      schema: yup.string().required('Qualio link is required.'),
      onSubmit: async (data) => {
        await createQualioInlineBlock.mutateAsync(
          { parent: folder._id, url: data.text },
          {
            onSuccess: (data) => {
              setSimpleDialogState({ ...dialogState, isOpen: false });
              // the Qualio snippet arrives completed, so it opens in view mode instead of creating a draft
              openItemInNewTab(data, {});
            },
          }
        );
      },
    };
    setSimpleDialogState(dialogState);
  };

  const [bindingModeDialog, setBindingModeDialog] = useState<TOptionsDialogState | undefined>(undefined);
  const hiddenFileInput = useRef<HTMLInputElement | null>(null);
  const hiddenFolderInput = useRef<HTMLInputElement | null>(null);

  const triggerUploadInput = (input: HTMLInputElement | null) => {
    setTimeout(() => {
      input?.click();
    }, 100);
  };

  const handleCreateFromTemplate = () => {
    if (!canCreateFromTemplate) return;

    const dialogState: TSelectItemDialogState = {
      isOpen: true,
      onSelect: async (items) => {
        const template = items[0];
        if (!template || template.type !== 'DOCUMENT' || !template.stats.approvedVersionId) return;

        const doCopy = async (bindingMode?: EBindingMode) => {
          // Dismiss both dialogs immediately so the user sees their choice was registered
          setBindingModeDialog(undefined);
          setTemplateDialogState(undefined);
          const hideProgress = showProgress(`Creating document from ${TERMINOLOGY.template.lower}...`);
          try {
            const data = await copy.mutateAsync({
              destinationFolderId: folder._id,
              items: [
                {
                  _id: template._id,
                  itemType: 'DOCUMENT',
                  templateVersionId: template.stats.approvedVersionId,
                  bindingMode,
                },
              ],
              successMessage: false,
            });
            if (data?.[0]) openItemInNewTab(data[0]);
          } finally {
            hideProgress();
          }
        };

        if (organization.featureSettings.templateBinding?.enabled) {
          setTemplateDialogState(undefined);
          setBindingModeDialog({
            isOpen: true,
            title: 'Create from template',
            options: getBindingModeOptions(
              () => doCopy(EBindingMode.BOUND),
              () => doCopy(EBindingMode.DETACHED)
            ),
            selectedValue: undefined,
          });
        } else {
          // Feature flag off: skip dialog, default to detached (existing behavior).
          doCopy();
        }
      },
    };

    setTemplateDialogState(dialogState);
  };

  const items = [
    {
      label: 'Folder',
      icon: <img src={folderIcon} />,
      onClick: handleFolderCreate,
    },
  ];

  if (user.groups.includes(USER_GROUPS.AUTHOR) && !countriesFolderType && !snapshotsFolderType) {
    if (isBlockFolderType) {
      [
        {
          label: TERMINOLOGY.block.upper,
          icon: <img src={block} />,
          onClick: handleDocCreate,
        },
        {
          label: TERMINOLOGY.inlineBlock.upper,
          icon: <img src={inlineBlock} />,
          onClick: handleInlineBlockCreate,
        },
      ].forEach((item) => items.push(item));
      if (organization.isQualioConfigured)
        items.push({
          label: `Qualio ${TERMINOLOGY.inlineBlock.lower}`,
          icon: <img src={inlineBlock} />,
          onClick: handleQualioInlineBlockCreate,
        });
    } else {
      const docActions = [
        {
          label: 'Document',
          icon: <img src={docx} />,
          onClick: handleDocCreate,
        },
      ];
      if (canCreateFromTemplate)
        docActions.push({
          label: `From ${TERMINOLOGY.template.lower}...`,
          icon: <img src={dotx} />,
          onClick: handleCreateFromTemplate,
        });
      docActions.push({
        label: 'Upload files',
        icon: <DocumentArrowUpRegular />,
        onClick: () => triggerUploadInput(hiddenFileInput.current),
      });
      docActions.push({
        label: 'Upload folder',
        icon: <FolderArrowUpRegular />,
        onClick: () => triggerUploadInput(hiddenFolderInput.current),
      });
      docActions.forEach((item) => items.push(item));
    }
  }

  const shouldRenderUploadInput = items.some((i) => i.label === 'Upload files');

  // primary button:
  let primaryButtonLabel = 'New & upload';
  if (isBlockFolderType) primaryButtonLabel = 'New';
  else if (items.length <= 1) primaryButtonLabel = newFolderLabel;
  const createDisabled =
    folder.accessRight < AccessType.EDIT ||
    (countriesFolderType === ECountriesFolderType.COUNTRY && !canCreateCountryRegistration);
  const renderPrimaryButton = (props?: ButtonProps) => (
    <Button appearance="primary" disabled={createDisabled} icon={<AddFilled />} {...props}>
      {primaryButtonLabel}
    </Button>
  );

  const classes = useStyles();

  return (
    // cannot use selectedItemIds because of archive / move
    <Card className={mergeClasses(classes.card, selectedItems.length <= 0 && classes.transparent)}>
      <OptionsDialog state={bindingModeDialog} setState={setBindingModeDialog} />
      {shouldRenderUploadInput && (
        <>
          <UploadInput hiddenFileInput={hiddenFileInput} multiple handleFiles={handleFilesUpload} />
          <UploadInput hiddenFileInput={hiddenFolderInput} directory multiple handleFiles={handleFilesUpload} />
        </>
      )}
      <div className="flex space-x-1">
        {items.length > 1 ? (
          <Menu>
            <MenuTrigger disableButtonEnhancement>{renderPrimaryButton()}</MenuTrigger>
            <MenuPopover>
              <MenuList>
                {items.map((item) => (
                  <MenuItem key={item.label} icon={item.icon} onClick={item.onClick}>
                    {item.label}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : (
          renderPrimaryButton({ onClick: handleFolderCreate })
        )}
        <div className="grow">
          <Overflow params={params} defaultAppearance="subtle" />
        </div>
        {selectedItems.length > 0 && (
          <Button icon={<DismissRegular />} onClick={() => setSelectedItemIds(new Set())}>
            {selectedItems.length} selected
          </Button>
        )}
      </div>
    </Card>
  );
};

export default Toolbar;
